import { sendMessage } from "./xd-events";
import * as projectActions from "../actions/project-actions";
import { calculateAreaSize, calculateLineLength } from './xd-math';
import convert from 'convert-units';

const CURRENT_VERSION = 'react-planner:1';

/**
* Received a floor plan to load.
*/
function loadProject(store, floorPlan) {
  console.log("Received floor plan data to load", floorPlan);
  if (!floorPlan)
    return;
  const { version, data } = floorPlan;
  if (version !== CURRENT_VERSION) {
    console.error("Unsupported version:", version, "Expected:", CURRENT_VERSION);
    return;
  }
  const action = projectActions.loadProject(data);
  store.dispatch(action);
}


function saveProject(scene) {
  const floorPlanData = {
    version: CURRENT_VERSION,
    data: scene.toJS(),
    estimates: calculateProjectEstimateFromScene(scene),
  };
  sendMessage({ type: 'save', floorPlanData });
}


function exitProject() {
  if (confirm("Are you sure you want to exit? Any unsaved changes will be lost.")) {
    sendMessage({ type: 'exit' });
  }
}

/**
  @typedef {{
    id: string
    length: number
    unit: string
  }} WallComputed
  @typedef {{
    id: string
    area: number
    unit: string
  }} AreaComputed
  @typedef {{
    id: string
    cost: number          // cost of this line item for all elements
    materialCost: number, // for non-labour items, total material/unit cost
    labourCost: number,   // for non-labour items, total labour/unit cost
    quantity: number      // total quantity of this line item in this project
    isLabour: boolean     // true if this line item is labour; quantities become "hours", object measurement become irrelevant, baseCost ignored
    walls: Array<{
      id: string          // id of the relevant element
      quantity: number    // qty of this line item on this element
      cost: number        // cost of this line item on this element (base * qty + wallLen * costPerUnit)
      labourCost: number  // labour cost for this line item on this element (wallLen * labourCostPerUnit)
    }>
    areas: Array<{
      id: string
      quantity: number
      cost: number
      labourCost: number
    }>
    items: Array<{
      id: string
      quantity: number
      cost: number
      labourCost: number
    }>
    holes: Array<{
      id: string
      quantity: number
      cost: number
      labourCost: number
    }>
    projectAdditional: {  // cost of this line item if used as an additional project line item
      quantity: number
      cost: number
    }
    details: {
      rawQuantity: number  // Unrounded total quantity
      materialExcess: number  // Excess percentage for material calculations
      walls: {
        totalQuantity: number
        totalCost: number
        totalLabourCost: number
      }
      areas: {
        totalQuantity: number
        totalCost: number
        totalLabourCost: number
      }
      items: {
        totalQuantity: number
        totalCost: number
      }
      holes: {
        totalQuantity: number
        totalCost: number
      }
    }
  }} LineItemComputed
  @typedef {{
    wallsComputed: Array<WallComputed>
    areasComputed: Array<AreaComputed>
    lineItems: Array<LineItemComputed>
    subTotal: number        // sub-total for whole project (sum of all line items costs)
  }} ProjectEstimate
 */


/**
 * @param {Scene} scene
 * @returns {Array<WallComputed>}
 */
function computeWallsFromScene(scene) {
  const computedLayers = scene.layers.entrySeq().map(([layerID, layer]) => {
    return layer.lines.entrySeq().map(([lineID, line]) => {
      const unit = scene.unit;
      const length = calculateLineLength(line, layer, unit);
      return {
        id: lineID,
        length,
        unit,
      };
    }).toJS();
  }).toJS();
  const merged = computedLayers.reduce((acc, layer) => acc.concat(layer), []);
  return merged;
}


/**
 * @param {Scene} scene
 * @returns {Array<AreaComputed>}
 */
function computeAreasFromScene(scene) {
  const computedLayers = scene.layers.entrySeq().map(([layerID, layer]) => {
    return layer.areas.entrySeq().map(([areaID, area]) => {
      const unit = scene.unit;
      const areaSize = calculateAreaSize(area, layer, unit);
      return {
        id: areaID,
        area: areaSize,
        unit,
      };
    }).toJS();
  }).toJS();
  const merged = computedLayers.reduce((acc, layer) => acc.concat(layer), []);
  return merged;
}


function computeWallsBreakdownForLineItem(scene, lineItem) {

}


/**
 * @param {Scene} scene
 * @param {Array<AreaComputed>?} areasComputed
 * @param {Array<WallComputed>?} wallsComputed
 * @returns {Array<LineItemComputed>}
 */
function computeLineItemsFromScene(scene, areasComputed, wallsComputed) {

  areasComputed = areasComputed || computeAreasFromScene(scene);
  wallsComputed = wallsComputed || computeWallsFromScene(scene);

  const allWalls = scene.layers.entrySeq().map(([layerID, layer]) => layer.lines).flatten(1);
  const allAreas = scene.layers.entrySeq().map(([layerID, layer]) => layer.areas).flatten(1);
  const allItems = scene.layers.entrySeq().map(([layerID, layer]) => layer.items).flatten(1);
  const allHoles = scene.layers.entrySeq().map(([layerID, layer]) => layer.holes).flatten(1);

  let excess = scene.getIn(['meta', 'config', 'materialExcess']);
  excess = 1 + (excess != null ? excess : 5) / 100;

  const lineItems = scene.lineItems.entrySeq().map(([lineItemID, lineItem]) => {
    const isLabour = lineItem.unit === 'hours';

    const walls = allWalls
      .filter(wall => wall.lineItems.some(ref => ref.id === lineItem.id))
      .map(wall => {
        const quantity =
          wall.lineItems
            .filter(ref => ref.id === lineItem.id)
            .reduce((acc, ref) => {
              if (isLabour) {
                // Ignore wall sides for labour
                return acc + ref.quantity;
              }
              let { quantity, bSideA, bSideB } = ref;
              bSideA = bSideA == null ? true : bSideA;
              bSideB = bSideB == null ? true : bSideB;
              if (quantity === 'auto') {
                const minLengthArea = lineItem.minLengthArea || 0;
                const minLengthAreaUnit = lineItem.minLengthAreaUnit || scene.unit || 'cm';
                const computedWall = wallsComputed.find(w => w.id === wall.id);
                if (!computedWall) throw new Error(`Wall with id ${wall.id} not found in computed walls`);
                const { length: wallLength, unit: wallLengthUnit } = computedWall;
                const wallLengthConverted = convert(wallLength).from(wallLengthUnit).to(minLengthAreaUnit);
                quantity = (wallLengthConverted * excess) / minLengthArea;
              }
              const nSides = (bSideA ? 1 : 0) + (bSideB ? 1 : 0);
              return acc + (quantity * nSides);
            }, 0);
        if (isLabour) {
          const cost = quantity * lineItem.costPerUnit;
          return {
            id: wall.id,
            quantity,
            cost,
          };
        } else {
          const computedWall = wallsComputed.find(w => w.id === wall.id);
          if (!computedWall) throw new Error(`Wall with id ${wall.id} not found in computed walls`);
          const wallLength =
            convert(computedWall.length)
              .from(computedWall.unit)
              .to(lineItem.unit || scene.unit || 'cm');
          const totalLengthCost = wallLength * lineItem.costPerUnit;
          const labourCost = wallLength * lineItem.labourCostPerUnit;
          const cost = quantity * totalLengthCost;
          return {
            id: wall.id,
            quantity,
            cost,
            labourCost,
          };
        }
      })
      .toJS();

    const areas = allAreas
      .filter(area => area.lineItems.some(ref => ref.id === lineItem.id))
      .map(area => {
        const quantity = area.lineItems
          .filter(ref => ref.id === lineItem.id)
          .reduce((acc, ref) => {
            let { quantity } = ref;
            if (quantity === 'auto') {
              const minLengthArea = lineItem.minLengthArea || 0;
              const minLengthAreaUnit = lineItem.minLengthAreaUnit || scene.unit || 'cm';
              const computedArea = areasComputed.find(a => a.id === area.id);
              if (!computedArea) throw new Error(`Area with id ${area.id} not found in computed areas`);
              const { area: areaSize, unit: areaSizeUnit } = computedArea;
              const areaSizeConverted = convert(areaSize).from(`${areaSizeUnit}2`).to(`${minLengthAreaUnit}2`);
              quantity = (areaSizeConverted * excess) / minLengthArea;
            }
            return acc + quantity;
          }, 0);
        if (isLabour) {
          const cost = quantity * lineItem.costPerUnit;
          return {
            id: area.id,
            quantity,
            cost,
          };
        } else {
          const computedArea = areasComputed.find(a => a.id === area.id);
          if (!computedArea) throw new Error(`Area with id ${area.id} not found in computed areas`);
          const areaSize =
            convert(computedArea.area)
              .from(`${computedArea.unit}2`)
              .to(`${lineItem.unit || scene.unit || 'cm'}2`);
          const totalAreaCost = areaSize * lineItem.costPerUnit;
          const labourCost = areaSize * lineItem.labourCostPerUnit;
          const cost = quantity * totalAreaCost;
          return {
            id: area.id,
            quantity,
            cost,
            labourCost,
          };
        }
      })
      .toJS();

    const items = allItems
      .filter(item => item.lineItems.some(ref => ref.id === lineItem.id))
      .map(item => {
        const quantity = item.lineItems
          .filter(ref => ref.id === lineItem.id)
          .reduce((acc, ref) => {
            if (ref.quantity === 'auto')
              throw new Error('Auto quantity not supported for items');
            return acc + ref.quantity;
          }, 0);
        const cost = isLabour ?
          (quantity * lineItem.costPerUnit) :
          0;
        return {
          id: item.id,
          quantity,
          cost,
        };
      })
      .toJS();

    const holes = allHoles
      .filter(hole => hole.lineItems.some(ref => ref.id === lineItem.id))
      .map(hole => {
        const quantity = hole.lineItems
          .filter(ref => ref.id === lineItem.id)
          .reduce((acc, ref) => {
            if (ref.quantity === 'auto')
              throw new Error('Auto quantity not supported for holes');
            return acc + ref.quantity;
          }, 0);
        const cost = isLabour ?
          (quantity * lineItem.costPerUnit) :
          0;
        return {
          id: hole.id,
          quantity,
          cost,
        };
      })
      .toJS();

    const additionalQuantity = scene.lineItemsAdditional
      .filter(ref => ref.id === lineItem.id)
      .reduce((acc, ref) => acc + ref.quantity, 0);

    const projectAdditional = {
      quantity: additionalQuantity,
      cost: isLabour ?
        (additionalQuantity * lineItem.costPerUnit) :
        0
    };

    const totalWallsQuantity = walls.reduce((acc, wall) => acc + wall.quantity, 0);
    const totalAreasQuantity = areas.reduce((acc, area) => acc + area.quantity, 0);
    const totalItemsQuantity = items.reduce((acc, item) => acc + item.quantity, 0);
    const totalHolesQuantity = holes.reduce((acc, hole) => acc + hole.quantity, 0);

    const rawTotalQuantity =
      totalWallsQuantity +
      totalAreasQuantity +
      totalItemsQuantity +
      totalHolesQuantity +
      projectAdditional.quantity;

    const totalQuantity = Math.ceil(rawTotalQuantity);

    const totalWallsCost = walls.reduce((acc, wall) => acc + wall.cost, 0);
    const totalAreasCost = areas.reduce((acc, area) => acc + area.cost, 0);
    const totalItemsCost = items.reduce((acc, item) => acc + item.cost, 0);
    const totalHolesCost = holes.reduce((acc, hole) => acc + hole.cost, 0);

    const subTotalMaterial = (
      totalWallsCost +
      totalAreasCost +
      totalItemsCost +
      totalHolesCost +
      totalQuantity * lineItem.baseCost) || 0;

    const totalWallsLabourCost = walls.reduce((acc, wall) => acc + wall.labourCost, 0);
    const totalAreasLabourCost = areas.reduce((acc, area) => acc + area.labourCost, 0);

    const subTotalLabour = (totalWallsLabourCost + totalAreasLabourCost) || 0;

    const subTotal = subTotalMaterial + subTotalLabour;

    return {
      id: lineItem.id,
      cost: subTotal,
      materialCost: subTotalMaterial,
      labourCost: subTotalLabour,
      quantity: totalQuantity,
      isLabour,
      walls,
      areas,
      items,
      holes,
      projectAdditional,
      details: {
        rawQuantity: rawTotalQuantity,
        materialExcess: excess,
        walls: {
          totalQuantity: totalWallsQuantity,
          totalCost: totalWallsCost,
          totalLabourCost: totalWallsLabourCost,
        },
        areas: {
          totalQuantity: totalAreasQuantity,
          totalCost: totalAreasCost,
          totalLabourCost: totalAreasLabourCost,
        },
        items: {
          totalQuantity: totalItemsQuantity,
          totalCost: totalItemsCost,
        },
        holes: {
          totalQuantity: totalHolesQuantity,
          totalCost: totalHolesCost,
        },
      },
    };
  });
  return lineItems.toJS();
}


/**
 * @param {Scene} scene
 * @returns {ProjectEstimate}
 */
function calculateProjectEstimateFromScene(scene) {
  const areasComputed = computeAreasFromScene(scene);
  const wallsComputed = computeWallsFromScene(scene);
  const lineItems = computeLineItemsFromScene(scene, areasComputed, wallsComputed);
  const subTotal = lineItems.reduce((acc, lineItem) => acc + lineItem.cost, 0);
  return {
    areasComputed,
    wallsComputed,
    lineItems,
    subTotal,
  };
}


export {
  loadProject,
  saveProject,
  exitProject,
  calculateProjectEstimateFromScene,
};
